import React, { useMemo, useState, lazy } from 'react'
import { Helmet } from 'react-helmet-async'
import { Typography, Grid, Autocomplete, TextField } from '@mui/material'
import moment from 'moment'
import { Navbar } from '../../../component/shared/Navbar'
import { PageContainer } from '../../../component/styled/PageContainer'
import Breadcrumb from '../../../component/shared/Breadcrumb'
import { useGetSalesVisualization } from '../../../api/sales-commission-incentive'
import { ExportButton } from '../../../component/shared/component/ExportButton'
import { LazyChartSection } from './LazyChartSection'
import { toCsv } from './exportCsv'

// Code-split each chart so recharts loads per-chunk instead of all up front
const CompanyBreakdownChart = lazy(() =>
  import('./CompanyBreakdownChart').then((m) => ({ default: m.CompanyBreakdownChart }))
)
const MonthlyBreakdownChart = lazy(() =>
  import('./MonthlyBreakdownChart').then((m) => ({ default: m.MonthlyBreakdownChart }))
)
const CompanyMonthlyChart = lazy(() =>
  import('./CompanyMonthlyChart').then((m) => ({ default: m.CompanyMonthlyChart }))
)
const RecipientBreakdownChart = lazy(() =>
  import('./RecipientBreakdownChart').then((m) => ({ default: m.RecipientBreakdownChart }))
)
const SalesIdBreakdownChart = lazy(() =>
  import('./SalesIdBreakdownChart').then((m) => ({ default: m.SalesIdBreakdownChart }))
)

const MIN_MONTH = '012026'

// Build month options from 012026 up to the current month (MMYYYY value, "MMMM YYYY" label)
const getMonthOptions = () => {
  const options = []
  const current = moment(MIN_MONTH, 'MMYYYY')
  const end = moment().startOf('month')

  while (current.isSameOrBefore(end, 'month')) {
    options.push({ label: current.format('MMMM YYYY'), value: current.format('MMYYYY') })
    current.add(1, 'month')
  }

  return options.reverse()
}

export const SalesVisualizationPage = () => {
  const monthOptions = useMemo(() => getMonthOptions(), [])

  const [startMonth, setStartMonth] = useState(MIN_MONTH)
  const [endMonth, setEndMonth] = useState(monthOptions[0].value)

  const { data = [], isLoading, isError } = useGetSalesVisualization({ startMonth, endMonth })

  return (
    <>
      <Helmet titleTemplate='%s | Staff Portal' defaultTitle='Staff Portal'>
        <title>Sales Visualization</title>
      </Helmet>
      <Navbar />
      <PageContainer>
        <Grid container spacing={3} paddingTop={5}>
          <Grid item xs={12}>
            <Breadcrumb items={[{ label: 'Visualization' }]} />
            <Typography variant='h1'>Sales Visualization</Typography>
            <Typography variant='body1' color='text.secondary'>
              Analytics dashboard for commissions and incentives
            </Typography>
          </Grid>

          <Grid item xs={12} display='flex' gap={2} alignItems='center'>
            <Autocomplete
              size='small'
              options={monthOptions}
              value={monthOptions.find((option) => option.value === startMonth) || null}
              onChange={(_, option) => option && setStartMonth(option.value)}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} variant='outlined' label='Start Month' />
              )}
              sx={{ width: 220 }}
              disableClearable
            />
            <Autocomplete
              size='small'
              options={monthOptions}
              value={monthOptions.find((option) => option.value === endMonth) || null}
              onChange={(_, option) => option && setEndMonth(option.value)}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} variant='outlined' label='End Month' />
              )}
              sx={{ width: 220 }}
              disableClearable
            />
            <ExportButton
              variant='outlined'
              sx={{ ml: 'auto' }}
              exportFn={() => ({ data: toCsv(data) })}
              filename={`Sales_Visualization_${startMonth}-${endMonth}.csv`}
            />
          </Grid>

          <Grid item xs={12}>
            <LazyChartSection>
              <CompanyBreakdownChart data={data} isLoading={isLoading} isError={isError} />
            </LazyChartSection>
          </Grid>

          <Grid item xs={12}>
            <LazyChartSection>
              <MonthlyBreakdownChart
                data={data}
                isLoading={isLoading}
                isError={isError}
                startMonth={startMonth}
                endMonth={endMonth}
              />
            </LazyChartSection>
          </Grid>

          <Grid item xs={12}>
            <LazyChartSection>
              <CompanyMonthlyChart
                data={data}
                isLoading={isLoading}
                isError={isError}
                startMonth={startMonth}
                endMonth={endMonth}
              />
            </LazyChartSection>
          </Grid>

          <Grid item xs={12}>
            <LazyChartSection>
              <RecipientBreakdownChart data={data} isLoading={isLoading} isError={isError} />
            </LazyChartSection>
          </Grid>

          <Grid item xs={12}>
            <LazyChartSection>
              <SalesIdBreakdownChart data={data} isLoading={isLoading} isError={isError} />
            </LazyChartSection>
          </Grid>
        </Grid>
      </PageContainer>
    </>
  )
}
